/**
 * SmartCalendar Component
 *
 * Calendar with color-coded availability for scheduling office lunches
 * - Green: Fully available (no conflicts)
 * - Yellow: Partially booked (some time slots taken)
 * - Red: Fully booked (no availability)
 * - Gray: Unavailable (day of week not available at location)
 */

import React, { useState, useEffect } from 'react';
import { availabilityService } from '../services/availabilityService';
import { customerLocationService } from '../services/locationService';
import { schedulingConflictService } from '../services/schedulingConflictService';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import { logger } from '../utils/logger';

const SmartCalendar = ({
  customerId,
  locationId,
  selectedDate,
  selectedTime,
  onDateTimeSelect,
  salesRepId // NEW: Sales rep ID for conflict detection
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [availableDays, setAvailableDays] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [conflictingSlots, setConflictingSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Load location availability (which days of week are available)
  useEffect(() => {
    const loadLocationAvailability = async () => {
      if (!customerId || !locationId) {
        logger.warn('SmartCalendar: Missing customerId or locationId', { customerId, locationId });
        setAvailableDays([]);
        return;
      }

      try {
        logger.debug('SmartCalendar: Loading location availability', { customerId, locationId });
        const customerLocations = await customerLocationService.getCustomerLocations(customerId);
        logger.debug('SmartCalendar: Customer locations loaded', { count: customerLocations?.length, locations: customerLocations });

        const currentLocation = customerLocations.find(loc => loc.location_id === locationId);
        logger.debug('SmartCalendar: Current location found', { currentLocation });

        if (currentLocation) {
          const days = [];
          if (currentLocation.works_sunday) days.push(0);
          if (currentLocation.works_monday) days.push(1);
          if (currentLocation.works_tuesday) days.push(2);
          if (currentLocation.works_wednesday) days.push(3);
          if (currentLocation.works_thursday) days.push(4);
          if (currentLocation.works_friday) days.push(5);
          if (currentLocation.works_saturday) days.push(6);

          logger.debug('SmartCalendar: Available days set', { days, daysOfWeek: days.map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]) });
          setAvailableDays(days);
        } else {
          logger.warn('SmartCalendar: Location not found in customer locations', { locationId, availableLocations: customerLocations?.map(l => l.location_id) });
          setAvailableDays([]);
        }
      } catch (error) {
        logger.error('SmartCalendar: Error loading location availability', { error: error.message });
        setAvailableDays([]);
      }
    };

    loadLocationAvailability();
  }, [customerId, locationId]);

  // Load calendar availability for current month
  useEffect(() => {
    const loadCalendarAvailability = async () => {
      if (!customerId || !locationId) {
        setLoading(false);
        return;
      }

      // If availableDays hasn't loaded yet, wait
      if (availableDays.length === 0) {
        logger.debug('SmartCalendar: Waiting for availableDays to load before loading calendar');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        logger.debug('SmartCalendar: Loading calendar availability for current month');

        // Get first and last day of visible month
        const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

        const startDate = firstDay.toISOString().split('T')[0];
        const endDate = lastDay.toISOString().split('T')[0];

        const dateAvailability = await availabilityService.getDateAvailabilityRange(
          customerId,
          locationId,
          startDate,
          endDate
        );

        setAvailability(dateAvailability);
      } catch (error) {
        logger.error('SmartCalendar: Error loading calendar availability', { error: error.message });
      } finally {
        setLoading(false);
      }
    };

    loadCalendarAvailability();
  }, [customerId, locationId, currentMonth, availableDays]);

  // Load time slots when date is selected
  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!selectedDate || !customerId) return;

      try {
        setLoadingTimeSlots(true);
        // For office lunches: check location-wide availability (not just organizer's schedule)
        // This prevents double-booking the same time slot at a location
        const slots = await availabilityService.getAvailableTimeSlots(
          customerId,
          selectedDate,
          locationId,
          ['11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM'],
          true  // checkLocationWide = true
        );

        // If salesRepId is provided, filter out conflicting time slots
        if (salesRepId && slots.length > 0) {
          logger.debug('SmartCalendar: Checking sales rep conflicts', {
            salesRepId,
            date: selectedDate,
            slotsToCheck: slots.length
          });

          const mergedAvailability = await schedulingConflictService.getMergedAvailability(
            salesRepId,
            selectedDate,
            slots
          );

          logger.debug('SmartCalendar: Merged availability result', {
            available: mergedAvailability.available.length,
            conflicting: mergedAvailability.conflicting.length,
            salesRepMeetings: mergedAvailability.salesRepMeetings.length
          });

          setTimeSlots(mergedAvailability.available);
          setConflictingSlots(mergedAvailability.conflicting);
        } else {
          // No conflict checking, show all available slots
          setTimeSlots(slots);
          setConflictingSlots([]);
        }
      } catch (error) {
        logger.error('Error loading time slots', { error: error.message });
        setTimeSlots([]);
        setConflictingSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    loadTimeSlots();
  }, [selectedDate, customerId, locationId, salesRepId]);

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getDateStatus = (date) => {
    if (!date) return null;

    // IMPORTANT: Use local date string, not UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const dayOfWeek = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Past dates
    if (date < today) {
      return 'past';
    }

    // Check if day of week is available at location
    if (!availableDays.includes(dayOfWeek)) {
      return 'unavailable';
    }

    // Check availability status from the availability object
    const status = availability[dateStr] || 'available';
    return status;
  };

  const getDateClassName = (date) => {
    if (!date) return '';

    const status = getDateStatus(date);

    // IMPORTANT: Use local date string, not UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const isSelected = selectedDate === dateStr;

    // Check if this is today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isToday = dateStr === todayStr;

    const baseClasses = 'relative w-full rounded-lg text-sm font-medium transition-all';

    if (isSelected) {
      return `${baseClasses} bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2`;
    }

    // Add blue border for today's date
    const todayBorder = isToday ? 'ring-2 ring-blue-400' : '';

    switch (status) {
      case 'past':
        return `${baseClasses} ${todayBorder} bg-gray-100 text-gray-400 cursor-not-allowed`;
      case 'unavailable':
        return `${baseClasses} ${todayBorder} bg-gray-200 text-gray-500 cursor-not-allowed`;
      case 'blocked':
        return `${baseClasses} ${todayBorder} bg-gray-300 text-gray-600 cursor-not-allowed`;
      case 'available':
        return `${baseClasses} ${todayBorder} bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer`;
      case 'partially_booked':
        return `${baseClasses} ${todayBorder} bg-yellow-100 text-yellow-800 hover:bg-yellow-200 cursor-pointer`;
      case 'fully_booked':
        return `${baseClasses} ${todayBorder} bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer`;
      default:
        return `${baseClasses} ${todayBorder} bg-white border border-gray-200 text-gray-700 hover:border-blue-300 cursor-pointer`;
    }
  };

  const handleDateClick = (date) => {
    if (!date) return;

    const status = getDateStatus(date);
    if (status === 'past' || status === 'unavailable' || status === 'blocked') return;

    // IMPORTANT: Use local date string, not UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    onDateTimeSelect(dateStr, null);
  };

  const handleTimeClick = (time) => {
    if (!selectedDate) return;
    onDateTimeSelect(selectedDate, time);
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Calendar header */}
      <div className="flex flex-col items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <CalendarIcon className="w-5 h-5 mr-2" />
          Select Date & Time
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="text-base font-semibold text-gray-900 min-w-[140px] text-center">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        {availableDays.length === 0 || loading || Object.keys(availability).length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading availability...</span>
          </div>
        ) : (
          <div key={`${currentMonth.getMonth()}-${currentMonth.getFullYear()}`} className="grid grid-cols-7 gap-1">
            {getDaysInMonth().map((date, index) => (
              <div key={index}>
                {date ? (
                  <button
                    onClick={() => handleDateClick(date)}
                    className={getDateClassName(date)}
                    disabled={['past', 'unavailable'].includes(getDateStatus(date))}
                    style={{width: '100%', height: '40px'}}
                  >
                    {date.getDate()}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-200 mr-2"></div>
          <span className="text-gray-700">Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200 mr-2"></div>
          <span className="text-gray-700">Partially Booked</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-red-100 border border-red-200 mr-2"></div>
          <span className="text-gray-700">Fully Booked</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-gray-200 mr-2"></div>
          <span className="text-gray-700">Unavailable</span>
        </div>
      </div>

      {/* Time slot selection */}
      {selectedDate && (
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-base font-semibold text-gray-900 mb-3">
            Select Time for {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </h4>

          {loadingTimeSlots ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : timeSlots.length > 0 ? (
            <div className="space-y-4">
              {/* Available time slots */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {timeSlots.map((time) => {
                  const isSelected = selectedTime === time;
                  return (
                    <button
                      key={time}
                      onClick={() => handleTimeClick(time)}
                      className={`
                        px-4 py-2 text-sm font-medium rounded-lg transition-all
                        ${isSelected
                          ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                          : 'bg-white border border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                        }
                      `}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>

              {/* Conflicting time slots (if any) */}
              {conflictingSlots.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <h5 className="text-sm font-semibold text-gray-900">
                      Times With Scheduling Conflicts
                    </h5>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-xs text-orange-800 mb-2">
                      These times conflict with your existing meetings:
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {conflictingSlots.map((conflict) => (
                        <div
                          key={conflict.time}
                          className="relative px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 border border-gray-300 text-gray-400 cursor-not-allowed"
                          title={`Conflicts with meeting: ${conflict.conflictsWith?.customer_name || 'Unknown'}`}
                        >
                          <span className="line-through">{conflict.time}</span>
                        </div>
                      ))}
                    </div>
                    {conflictingSlots.length > 0 && conflictingSlots[0].conflictsWith && (
                      <p className="text-xs text-orange-700 mt-2">
                        Conflicting meeting: {conflictingSlots[0].conflictsWith.customer_name || 'Unknown customer'}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : conflictingSlots.length > 0 ? (
            <div className="space-y-4">
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No available time slots for this date.</p>
                <p className="text-sm text-gray-500 mt-1">The physician may have meetings scheduled.</p>
              </div>

              {/* Show conflicts even when no available slots */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  <h5 className="text-sm font-semibold text-gray-900">
                    Times With Scheduling Conflicts
                  </h5>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-800 mb-2">
                    These times conflict with your existing meetings:
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {conflictingSlots.map((conflict) => (
                      <div
                        key={conflict.time}
                        className="relative px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 border border-gray-300 text-gray-400 cursor-not-allowed"
                        title={`Conflicts with meeting: ${conflict.conflictsWith?.customer_name || 'Unknown'}`}
                      >
                        <span className="line-through">{conflict.time}</span>
                      </div>
                    ))}
                  </div>
                  {conflictingSlots.length > 0 && conflictingSlots[0].conflictsWith && (
                    <p className="text-xs text-orange-700 mt-2">
                      Conflicting meeting: {conflictingSlots[0].conflictsWith.customer_name || 'Unknown customer'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No available time slots for this date.</p>
              <p className="text-sm text-gray-500 mt-1">The physician may have meetings scheduled.</p>
            </div>
          )}
        </div>
      )}

      {/* Selection summary */}
      {selectedDate && selectedTime && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Selected:</strong>{' '}
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}{' '}
            at {selectedTime}
          </p>
        </div>
      )}
    </div>
  );
};

export default SmartCalendar;
